import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { ArrowLeft, Link, Check, Download, Bot, ChevronDown, Plus, Pencil, Trash2, GripVertical } from 'lucide-react';
import { motion, useScroll, useTransform, Reorder } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ClassForm from '../components/classroom/ClassForm';
import { BlueprintBasic, getClassBlueprints, attachBlueprintsToClass } from '../components/classroom/classBlueprintUtils';
import { ToolBasic, getClassTools, attachToolsToClass } from '../components/classroom/classToolUtils';
import { fetchFolders } from '../components/classroom/folders/folderUtils';
import type { Category } from '../components/classroom/types';
import { useClassProgress, ClassProgressStatus } from '../hooks/useClassProgress';

// Type definitions
type ClassData = {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  video_url: string;
  categories: string[];
  created_at: string;
  class_categories?: { category_id: string }[]; // For API response
};

// Removed old VideoState/Chapter types since built-in YouTube controls are used

// Type for dominant colors removed since we're using a fixed background color

// Helper function to extract video ID from URL
function extractVideoId(url: string): string | null {
  if (!url.includes('youtube.com')) return null;
  try {
    return new URL(url).searchParams.get('v');
  } catch {
    return null;
  }
}

const ClassDetails = () => {
  // 1. State hooks
  const { classId } = useParams<{ classId: string }>();
  const [searchParams] = useSearchParams();
  const folderId = searchParams.get('folder');
  const navigate = useNavigate();
  const [classData, setClassData] = useState<ClassData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [blueprints, setBlueprints] = useState<BlueprintBasic[]>([]);
  const [loadingBlueprints, setLoadingBlueprints] = useState(false);
  const [tools, setTools] = useState<ToolBasic[]>([]);
  const [loadingTools, setLoadingTools] = useState(false);
  const [relatedClasses, setRelatedClasses] = useState<ClassData[]>([]);
  const [progressMap, setProgressMap] = useState<Record<string, ClassProgressStatus>>({});
  const [mobileListOpen, setMobileListOpen] = useState(true);
  const [overlayVisible, setOverlayVisible] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showClassForm, setShowClassForm] = useState(false);
  const [editingClass, setEditingClass] = useState<ClassData | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderList, setReorderList] = useState<ClassData[]>([]);
  
  
  // 2. Refs
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLIFrameElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  
  // 3. Other hooks
  const { scrollY } = useScroll();
  const titleOpacity = useTransform(scrollY, [0, 200], [0.7, 1]);
  // Per-user class progress for current class
  const { status, ensureStarted, markCompleted, markStarted } = useClassProgress(classId);
  
  // Hide the custom play overlay when class is already started/completed
  useEffect(() => {
    if (status === 'started' || status === 'completed') {
      setOverlayVisible(false);
    } else {
      setOverlayVisible(true);
    }
  }, [status]);
  
  // 4. Effect for CSS animations
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes ripple {
        0% { transform: scale(0); opacity: 0.7; }
        100% { transform: scale(1); opacity: 0; }
      }
      
      @keyframes progress {
        0% { stroke-dashoffset: 628; }
        100% { stroke-dashoffset: 0; }
      }
      
      .chapter-transition {
        animation: flash 0.5s ease-out;
      }
      
      @keyframes flash {
        0% { stroke-opacity: 0.4; }
        50% { stroke-opacity: 0.9; }
        100% { stroke-opacity: 0.6; }
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  // Admin and categories fetch
  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('is_admin')
            .eq('id', user.id)
            .single();
          setIsAdmin(!!profile?.is_admin);
        }
      } catch {
        setIsAdmin(false);
      }
      try {
        const cats = await fetchFolders();
        setCategories(cats as any);
      } catch {}
    })();
  }, []);
  
  // Temporarily disable video minimization to fix scroll issues
  // useEffect(() => {
  //   const handleScroll = () => {
  //     const scrollPosition = window.scrollY;
  //     setVideoState(prev => ({
  //       ...prev,
  //       isMinimized: scrollPosition > scrollThreshold
  //     }));
  //   };

  // Admin: delete class helper
  const handleDeleteClass = async (id: string) => {
    if (!isAdmin) return;
    if (!confirm('Delete this class? This cannot be undone.')) return;
    try {
      await supabase.from('class_categories').delete().eq('class_id', id);
      await supabase.from('class_blueprints').delete().eq('class_id', id);
      await supabase.from('class_tools').delete().eq('class_id', id);
      const { error } = await supabase.from('classes').delete().eq('id', id);
      if (error) throw error;
      if (classData?.id === id) {
        navigate('/classroom');
      } else {
        setRelatedClasses(prev => prev.filter(c => c.id !== id));
      }
    } catch (e) {
      console.error('Delete failed', e);
      alert('Failed to delete class');
    }
  };
  //   
  //   window.addEventListener('scroll', handleScroll);
  //   return () => window.removeEventListener('scroll', handleScroll);
  // }, [scrollThreshold]);
  
  // 6. Removed prefers-reduced-motion tracking (not used)

  // This function is no longer needed since we're using a fixed background color
  
  // 8. Data fetching effect
  useEffect(() => {
    const fetchBlueprints = async (classId: string) => {
      try {
        setLoadingBlueprints(true);
        const blueprintsData = await getClassBlueprints(classId);
        setBlueprints(blueprintsData);
      } catch (err) {
        console.error('Error fetching blueprints:', err);
        // Don't set an error for blueprints - just show empty state
      } finally {
        setLoadingBlueprints(false);
      }
    };

    const fetchTools = async (classId: string) => {
      try {
        setLoadingTools(true);
        const toolsData = await getClassTools(classId);
        setTools(toolsData);
      } catch (err) {
        console.error('Error fetching tools:', err);
        // Don't set an error for tools - just show empty state
      } finally {
        setLoadingTools(false);
      }
    };
    
    const fetchClassData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!classId) {
          throw new Error('No class ID provided');
        }
        
        // Fetch class data with its categories
        const { data, error } = await supabase
          .from('classes')
          .select(`
            *,
            class_categories(category_id)
          `)
          .eq('id', classId)
          .single();
        
        if (error) throw error;
        
        if (!data) {
          throw new Error('Class not found');
        }
        
        // Format the data
        const formattedClass: ClassData = {
          ...data,
          categories: data.class_categories?.map((cc: any) => cc.category_id) || []
        };
        
        setClassData(formattedClass);
        
        // Increment view counter
        await supabase.rpc('increment_view_counter', {
          table_name: 'classes',
          record_id: classId
        });
        
        // Fetch blueprints and tools for this class
        fetchBlueprints(classId);
        fetchTools(classId);

        // Fetch classes for the selected folder (from ?folder=) ordered by position
        if (folderId) {
          let related: ClassData[] = [];
          try {
            const { data: relData, error: relErr } = await supabase
              .from('class_categories')
              .select(`
                position,
                classes (*, class_categories(category_id))
              `)
              .eq('category_id', folderId)
              .order('position', { ascending: true, nullsFirst: false });
            if (relErr) throw relErr;
            related = (relData || []).map((row: any) => ({
              ...row.classes,
              categories: row.classes?.class_categories?.map((cc: any) => cc.category_id) || []
            }));
          } catch (e) {
            // Fallback without position if column missing
            const { data: relClasses, error: relErr2 } = await supabase
              .from('classes')
              .select('*, class_categories(category_id)')
              .in('id', (
                await supabase
                  .from('class_categories')
                  .select('class_id')
                  .eq('category_id', folderId)
              ).data?.map((r: any) => r.class_id) || []);
            if (relErr2) throw relErr2;
            related = (relClasses || []).map((row: any) => ({
              ...row,
              categories: row.class_categories?.map((cc: any) => cc.category_id) || []
            }));
          }
          setRelatedClasses(related);
          const { data: userData } = await supabase.auth.getUser();
          const userId = userData.user?.id;
          if (userId && related.length > 0) {
            const ids = related.map((c: ClassData) => c.id);
            const { data: prog, error: progErr } = await supabase
              .from('user_class_progress')
              .select('class_id, status')
              .eq('user_id', userId)
              .in('class_id', ids);
            if (progErr) throw progErr;
            const map: Record<string, ClassProgressStatus> = {};
            (prog || []).forEach(r => { map[(r as any).class_id] = (r as any).status; });
            setProgressMap(map);
          }
        } else {
          // If no folder is provided, do not show random/all classes in sidebar
          setRelatedClasses([]);
          setProgressMap({});
        }
      } catch (err: any) {
        console.error('Error fetching class data:', err);
        setError(err.message || 'Failed to load class');
      } finally {
        setLoading(false);
      }
    };
    
    fetchClassData();
  }, [classId]);

  // 9. Event handlers
  const handleCopyLink = (e: React.MouseEvent) => {
    // Create ripple effect on button click relative to its wrapper
    const wrapper = (e.currentTarget as HTMLElement).parentElement;
    if (wrapper) {
      const rect = wrapper.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const ripple = document.createElement('span');
      ripple.classList.add('absolute', 'bg-[#c9fffc]', 'opacity-30', 'rounded-full');
      ripple.style.width = '120px';
      ripple.style.height = '120px';
      ripple.style.left = `${x - 60}px`;
      ripple.style.top = `${y - 60}px`;
      ripple.style.transform = 'scale(0)';
      ripple.style.animation = 'ripple 0.6s linear';

      wrapper.appendChild(ripple);
      setTimeout(() => ripple.remove(), 700);
    }

    // Copy link to clipboard
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    });
  };
  
  // Video player controls have been removed as they aren't functional
  
  // 10. Prepare video ID for YouTube embedding
  const videoId = classData?.video_url ? extractVideoId(classData.video_url) : null;
  const isStreamYard = classData?.video_url ? /streamyard\.com\//.test(classData.video_url) : false;
  const streamYardId = isStreamYard ? (() => {
    try {
      const u = new URL(classData!.video_url);
      return u.pathname.replace(/^\/e\//, '').replace(/^\//, '');
    } catch { return null; }
  })() : null;
  const embedSrc = isStreamYard
    ? (streamYardId ? `https://streamyard.com/e/${streamYardId}` : null)
    : (videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=0&controls=1&modestbranding=1&rel=0&showinfo=0&enablejsapi=1&playsinline=1&origin=${encodeURIComponent(window.location.origin)}` : null);

  // Removed duplicate related-classes effect (now included in main fetch)

  const groupedRelated = useMemo(() => {
    const result: Record<ClassProgressStatus, ClassData[]> = {
      not_started: [],
      started: [],
      completed: [],
    };
    relatedClasses.forEach(c => {
      const s = progressMap[c.id] ?? (c.id === classData?.id ? (status || 'not_started') : 'not_started');
      result[s].push(c);
    });
    return result;
  }, [relatedClasses, progressMap, status, classData?.id]);

  // 11. Custom markdown renderer
  const components = {
    a: ({ href, ...props }: { href?: string; [key: string]: any }) => {
      if (!href) return <span {...props} />;
      
      const isAbsolute = /^https?:\/\//i.test(href);
      const absoluteUrl = isAbsolute ? href : `https://${href}`;

      return (
        <a 
          className="text-[#c9fffc] hover:text-[#a0f0ff] underline"
          href={absoluteUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          {...props} 
        />
      );
    }
  };

  // Early return for loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0E1621] to-[#141C28] p-6 sm:p-8 md:p-12">
        <div className="animate-pulse bg-[#151D29] rounded-xl p-8 max-w-5xl mx-auto">
          <div className="h-8 bg-[#1a2533] rounded-full w-1/3 mb-8"></div>
          <div className="aspect-video bg-[#1a2533] rounded-xl mb-8"></div>
          <div className="h-6 bg-[#1a2533] rounded-full w-full mb-4"></div>
          <div className="h-6 bg-[#1a2533] rounded-full w-5/6 mb-4"></div>
          <div className="h-6 bg-[#1a2533] rounded-full w-4/6 mb-4"></div>
        </div>
      </div>
    );
  }

  if (error || !classData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0E1621] to-[#141C28] p-6 sm:p-8 md:p-12">
        <div className="bg-[#151D29] rounded-xl p-8 max-w-5xl mx-auto text-center">
          <h2 className="text-2xl text-white font-medium mb-4">Error</h2>
          <p className="text-gray-300">{error || 'Could not load class details'}</p>
          <button 
            onClick={() => navigate('/classroom')}
            className="mt-6 px-6 py-2 bg-[#1a2533] text-[#c9fffc] rounded-lg hover:bg-[#232f3e] transition-colors"
          >
            Back to Classroom
          </button>
        </div>
      </div>
    );
  }
  
  // Main render for successful state with split layout (sidebar + content)
  return (
    <motion.div 
      className="min-h-screen text-white relative overflow-x-hidden w-full max-w-full px-2 sm:px-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.33, 1, 0.68, 1] }}
    >
      <div className="max-w-[1400px] mx-auto pt-6 px-3 sm:px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Left sidebar (desktop only): related classes grouped by status */}
        <aside className="hidden md:block md:col-span-4 lg:col-span-3 bg-[#151D29]/80 rounded-xl border border-white/5 overflow-hidden">
          <div className="p-4 flex items-center justify-between border-b border-white/5">
            <button
              onClick={() => navigate('/classroom')}
              className="inline-flex items-center text-white text-sm bg-black/20 backdrop-blur-sm px-3 py-1.5 rounded-lg hover:bg-black/30 transition-colors"
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back
            </button>
            {isAdmin ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setEditingClass(null); setShowClassForm(true); }}
                  className="inline-flex items-center justify-center text-white text-sm bg-[#1a2533] w-9 h-9 rounded-lg hover:bg-[#232f3e] transition-colors"
                  title="Add Class"
                  aria-label="Add Class"
                >
                  <Plus className="w-4 h-4" />
                </button>
                {folderId && (
                  <button
                    onClick={() => {
                      setReorderMode((prev) => {
                        const next = !prev;
                        if (next) {
                          setReorderList(relatedClasses.slice());
                        }
                        return next;
                      });
                    }}
                    className={`inline-flex items-center justify-center text-white text-sm w-9 h-9 rounded-lg transition-colors ${reorderMode ? 'bg-amber-600/30 hover:bg-amber-600/40' : 'bg-[#1a2533] hover:bg-[#232f3e]'}`}
                    title={reorderMode ? 'Finish Reorder' : 'Reorder'}
                    aria-label={reorderMode ? 'Finish Reorder' : 'Reorder'}
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-300">Your progress</div>
            )}
          </div>
          {!reorderMode && (
            <div className="divide-y divide-white/5">
              {(['not_started','started','completed'] as ClassProgressStatus[]).map((groupKey) => (
                <div key={groupKey} className="">
                  <div className="px-4 py-2 text-xs uppercase tracking-wide text-gray-400">
                    {groupKey === 'not_started' ? 'Not Started' : groupKey === 'started' ? 'Started' : 'Completed'}
                  </div>
                  {(groupedRelated[groupKey] || []).map((c) => {
                    const s = progressMap[c.id] ?? (c.id === classData?.id ? status : 'not_started');
                    const isActive = c.id === classData?.id;
                    return (
                      <button
                        key={c.id}
                        onClick={() => navigate(`/classroom/${c.id}${folderId ? `?folder=${folderId}` : ''}`)}
                        className={`group w-full px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors text-left ${isActive ? 'bg-white/5' : ''}`}
                      >
                        <img src={c.thumbnail} alt="" className="w-14 h-9 rounded object-cover flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-sm font-medium">{c.title}</div>
                          <div className="text-xs text-gray-400">
                            {s === 'completed' && <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#9af7f1] inline-block"></span>Completed</span>}
                            {s === 'started' && <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full border border-[#9af7f1] inline-block"></span>Started</span>}
                            {s === 'not_started' && <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full border border-gray-400 inline-block"></span>Not Started</span>}
                          </div>
                        </div>
                        {isAdmin && (
                          <span
                            onClick={async (e) => { e.stopPropagation(); await handleDeleteClass(c.id); }}
                            title="Delete class"
                            className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center w-8 h-8 rounded-md bg-red-600/20 hover:bg-red-600/30 text-red-200 shrink-0"
                            role="button"
                            aria-label="Delete class"
                          >
                            <Trash2 className="w-4 h-4" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {reorderMode && (
            <div className="p-3">
              <div className="text-xs text-gray-400 mb-2">Drag to reorder classes in this folder</div>
              <Reorder.Group axis="y" values={reorderList} onReorder={setReorderList} className="space-y-2">
                {reorderList.map((item) => (
                  <Reorder.Item key={item.id} value={item} className="flex items-center gap-3 px-3 py-2 bg-[#0f1520] rounded-lg border border-white/5 shadow-sm">
                    <GripVertical className="w-4 h-4 text-gray-400" />
                    <img src={item.thumbnail} alt="" className="w-12 h-8 rounded object-cover" />
                    <div className="truncate text-sm">{item.title}</div>
                  </Reorder.Item>
                ))}
              </Reorder.Group>
              <div className="mt-3 flex justify-end gap-2">
                <button
                  onClick={() => { setReorderMode(false); setReorderList([]); }}
                  className="px-3 py-1.5 text-sm rounded-lg bg-[#1a2533] hover:bg-[#232f3e]"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!folderId) return;
                    try {
                      const ids = reorderList.map((c) => c.id);
                      const { error: rpcErr } = await supabase.rpc('set_class_order', {
                        p_category: folderId,
                        p_class_ids: ids,
                      });
                      if (rpcErr) throw rpcErr;

                      // Refetch to confirm saved order
                      const { data: relData, error: relErr } = await supabase
                        .from('class_categories')
                        .select(`
                          position,
                          classes (*, class_categories(category_id))
                        `)
                        .eq('category_id', folderId)
                        .order('position', { ascending: true, nullsFirst: false });
                      if (relErr) throw relErr;
                      const refreshed: ClassData[] = (relData || []).map((row: any) => ({
                        ...row.classes,
                        categories: row.classes?.class_categories?.map((cc: any) => cc.category_id) || []
                      }));
                      setRelatedClasses(refreshed);
                      setReorderMode(false);
                    } catch (e) {
                      console.error('Failed to save order', e);
                      alert('Failed to save order. Ensure class_categories.position exists and you have permission to update.');
                    }
                  }}
                  className="px-3 py-1.5 text-sm rounded-lg bg-[#9af7f1] text-gray-900 hover:opacity-90"
                >
                  Save
                </button>
              </div>
            </div>
          )}
        </aside>

        {/* Right content */}
        <div className="md:col-span-8 lg:col-span-9">
          {/* Mobile top header */}
          <div className="md:hidden flex items-center justify-between mt-2 mb-3 relative z-20 pt-[env(safe-area-inset-top)]">
            <button
              onClick={() => navigate('/classroom')}
              className="inline-flex items-center text-white text-sm bg-black/20 backdrop-blur-sm px-3 py-1.5 rounded-lg hover:bg-black/30 transition-colors"
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back
            </button>
            <motion.div className="relative overflow-hidden">
              <motion.button
                onClick={handleCopyLink}
                className="flex items-center bg-[#1a2533] rounded-lg px-2.5 py-1.5 text-[#c9fffc] hover:bg-[#232f3e] transition-colors overflow-hidden text-xs"
              >
                {copiedLink ? (<><Check className="mr-1.5 w-4 h-4" /><span>Copied</span></>) : (<><Link className="mr-1.5 w-4 h-4" /><span>Copy Link</span></>)}
              </motion.button>
            </motion.div>

          </div>

          {/* Video Container with Glow Effect */}
          <div ref={videoContainerRef} className="relative mt-2">
            <motion.div 
              className="relative aspect-video rounded-xl overflow-hidden"
              initial={{ scale: 1.03, opacity: 0 }}
              animate={{ 
                scale: 1, 
                opacity: 1,
                transition: { duration: 0.8, ease: [0.33, 1, 0.68, 1] }
              }}
            >
              <div className="absolute inset-0 sm:-m-2 rounded-xl bg-[#c9fffc] opacity-30 blur-xl"></div>
              {embedSrc && (
                <iframe
                  ref={videoRef}
                  className="absolute inset-0 w-full h-full"
                  src={embedSrc}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  frameBorder="0"
                ></iframe>
              )}
              {overlayVisible && (
                <button
                  onClick={() => {
                    try { ensureStarted(); } catch {}
                    setOverlayVisible(false);
                    if (!isStreamYard) {
                      // YouTube: small delay to ensure iframe is ready, then send play command
                      setTimeout(() => {
                        try {
                          const win = videoRef.current?.contentWindow;
                          if (win) {
                            win.postMessage(JSON.stringify({ event: 'command', func: 'playVideo', args: [] }), '*');
                          }
                        } catch {}
                      }, 100);
                    }
                  }}
                  className="absolute inset-0 flex items-center justify-center"
                  aria-label="Play video"
                  title="Play"
                >
                  <span className="relative z-10 inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-black/60 border border-white/20 text-white shadow-xl">
                    <svg className="w-8 h-8 translate-x-0.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
                  </span>
                </button>
              )}
            </motion.div>
          </div>

          {/* Mobile status + related classes panel (collapsible) */}
          <div className="md:hidden mt-4 sticky top-2 z-10">
            <div className="bg-[#151D29]/90 backdrop-blur-lg rounded-xl border border-white/5 shadow-xl">
              <div className="p-4 flex items-center justify-between">
                <button
                  onClick={() => setMobileListOpen(v => !v)}
                  className="flex items-center gap-3 text-left"
                >
                  <ChevronDown className={`w-5 h-5 text-gray-300 transition-transform ${mobileListOpen ? 'rotate-180' : ''}`} />
                  <div>
                    <div className="text-sm font-semibold text-white">
                      {status === 'completed' ? 'Completed' : status === 'started' ? 'Started' : 'Not Started'}
                    </div>
                    <div className="text-xs text-gray-400">
                      {status === 'started' ? 'In progress' : status === 'completed' ? 'Finished' : 'Ready to begin'}
                    </div>
                  </div>
                </button>
                <div className="flex items-center gap-2">
                  {status === 'not_started' && (
                    <button onClick={ensureStarted} className="px-3 py-1.5 rounded-lg bg-[#9af7f1] text-gray-900 font-medium hover:opacity-90 text-sm">Start</button>
                  )}
                  {status === 'started' && (
                    <button onClick={markCompleted} className="px-3 py-1.5 rounded-lg bg-[#9af7f1] text-gray-900 font-medium hover:opacity-90 text-sm">Mark Complete</button>
                  )}
                  {status === 'completed' && (
                    <button onClick={markStarted} className="px-3 py-1.5 rounded-lg bg-[#1a2533] text-white hover:bg-[#232f3e] text-sm">Mark as Started</button>
                  )}
                </div>
              </div>
              {mobileListOpen && (
                <div className="px-2 pb-3">
                  {(['not_started','started','completed'] as ClassProgressStatus[]).map((groupKey) => (
                    <div key={`m-${groupKey}`} className="">
                      <div className="px-2 py-2 text-xs uppercase tracking-wide text-gray-400">
                        {groupKey === 'not_started' ? 'Not Started' : groupKey === 'started' ? 'Started' : 'Completed'}
                      </div>
                      {(groupedRelated[groupKey] || []).map((c) => {
                        const s = progressMap[c.id] ?? (c.id === classData?.id ? status : 'not_started');
                        const isActive = c.id === classData?.id;
                        return (
                          <button
                            key={`m-${c.id}`}
                            onClick={() => navigate(`/classroom/${c.id}${folderId ? `?folder=${folderId}` : ''}`)}
                            className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-white/5 transition-colors text-left rounded-lg ${isActive ? 'bg-white/5' : ''}`}
                          >
                            <img src={c.thumbnail} alt="" className="w-14 h-9 rounded object-cover flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 text-xs text-gray-400">
                                {s === 'completed' && <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#9af7f1] inline-block"></span>Completed</span>}
                                {s === 'started' && <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full border border-[#9af7f1] inline-block"></span>Started</span>}
                                {s === 'not_started' && <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full border border-gray-400 inline-block"></span>Not Started</span>}
                              </div>
                              <div className="truncate text-sm font-medium text-white mt-0.5">{c.title}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Info panel */}
          <motion.div
            ref={contentRef}
            className="mt-6 bg-[#151D29]/90 backdrop-blur-lg rounded-xl overflow-hidden shadow-xl border border-white/5"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, ease: [0.33, 1, 0.68, 1] }}
          >
            <div className="p-6">
              <div className="mb-4 flex items-center justify-between gap-3">
                <motion.h1 
                  className="text-[20px] leading-[28px] font-medium text-white tracking-tight"
                  style={{ opacity: titleOpacity }}
                >
                  {classData.title}
                </motion.h1>
                <motion.div className="relative overflow-hidden shrink-0">
                  <motion.button
                    onClick={handleCopyLink}
                    className="flex items-center bg-[#1a2533] rounded-md px-2.5 py-1.5 text-[#c9fffc] hover:bg-[#232f3e] transition-colors overflow-hidden text-xs"
                    title="Copy link"
                    aria-label="Copy link"
                  >
                    {copiedLink ? (<><Check className="mr-1 w-4 h-4" /><span>Copied</span></>) : (<><Link className="mr-1 w-4 h-4" /><span>Copy</span></>)}
                  </motion.button>
                </motion.div>
              </div>

              {isAdmin && (
                <div className="mb-4 flex items-center gap-2">
                  <button
                    onClick={() => { setEditingClass(classData as ClassData); setShowClassForm(true); }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-[#1a2533] hover:bg-[#232f3e]"
                  >
                    <Pencil className="w-4 h-4" /> Edit
                  </button>
                  <button
                    onClick={() => { setEditingClass(null); setShowClassForm(true); }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-[#1a2533] hover:bg-[#232f3e]"
                  >
                    <Plus className="w-4 h-4" /> Add Class
                  </button>
                  <button
                    onClick={async () => {
                      if (!classData) return;
                      await handleDeleteClass(classData.id);
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-red-600/20 hover:bg-red-600/30 text-red-200"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              )}

              <motion.div 
                className="prose prose-invert max-w-none text-[14px] leading-[22px] tracking-tight"
                initial={{ y: 8, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.3, ease: [0.33, 1, 0.68, 1] }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                  {classData.description || ''}
                </ReactMarkdown>
              </motion.div>

              {/* Blueprints */}
              {blueprints.length > 0 && (
                <div className="mt-8 pt-6 border-t border-white/10">
                  <h3 className="text-[16px] font-medium text-white mb-4 flex items-center">
                    <Download className="mr-2 w-4 h-4" />
                    Available Blueprints
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {blueprints.map((blueprint) => (
                      <div key={blueprint.id} className="bg-[#1a2533] p-4 rounded-lg border border-gray-700 hover:border-[#c9fffc]/50 transition-colors">
                        <div className="flex items-start gap-3">
                          {blueprint.logo_url ? (
                            <img src={blueprint.logo_url} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-12 h-12 bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
                              <Bot className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1">
                            <h4 className="font-medium text-white">{blueprint.title}</h4>
                            <p className="text-gray-300 text-sm mt-1 line-clamp-2">{blueprint.description}</p>
                            <a href={blueprint.download_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-[#c9fffc] mt-3 hover:underline group">
                              <Download className="w-4 h-4 group-hover:scale-110 transition-transform" />
                              Download Blueprint
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {loadingBlueprints && (
                <div className="mt-8 pt-6 border-t border-white/10">
                  <h3 className="text-[16px] font-medium text-white mb-4 flex items-center">
                    <Download className="mr-2 w-4 h-4" />
                    Loading Blueprints...
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[1, 2].map(i => (
                      <div key={i} className="bg-[#1a2533] p-4 rounded-lg border border-gray-700 animate-pulse">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 bg-gray-700 rounded flex-shrink-0" />
                          <div className="flex-1">
                            <div className="h-5 bg-gray-700 rounded w-2/3 mb-2" />
                            <div className="h-3 bg-gray-700 rounded w-full mb-1" />
                            <div className="h-3 bg-gray-700 rounded w-4/5" />
                            <div className="h-4 bg-gray-700 rounded w-1/3 mt-3" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tools */}
              {tools.length > 0 && (
                <div className="mt-8 pt-6 border-t border-white/10">
                  <h3 className="text-[16px] font-medium text-white mb-4 flex items-center">
                    <Bot className="mr-2 w-4 h-4" />
                    Available AI Tools
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {tools.map((tool) => (
                      <div key={tool.id} className="bg-[#1a2533] p-4 rounded-lg border border-gray-700 hover:border-[#c9fffc]/50 transition-colors">
                        <div className="flex items-start gap-3">
                          {tool.logo_url ? (
                            <img src={tool.logo_url} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-12 h-12 bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
                              <Bot className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1">
                            <h4 className="font-medium text-white">{tool.title}</h4>
                            <p className="text-gray-300 text-sm mt-1 line-clamp-2">{tool.description}</p>
                            <a href={tool.tool_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-[#c9fffc] mt-3 hover:underline group">
                              <Bot className="w-4 h-4 group-hover:scale-110 transition-transform" />
                              Open Tool
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {loadingTools && (
                <div className="mt-8 pt-6 border-t border-white/10">
                  <h3 className="text-[16px] font-medium text-white mb-4 flex items-center">
                    <Bot className="mr-2 w-4 h-4" />
                    Loading AI Tools...
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[1, 2].map(i => (
                      <div key={i} className="bg-[#1a2533] p-4 rounded-lg border border-gray-700 animate-pulse">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 bg-gray-700 rounded flex-shrink-0" />
                          <div className="flex-1">
                            <div className="h-5 bg-gray-700 rounded w-2/3 mb-2" />
                            <div className="h-3 bg-gray-700 rounded w-full mb-1" />
                            <div className="h-3 bg-gray-700 rounded w-4/5" />
                            <div className="h-4 bg-gray-700 rounded w-1/3 mt-3" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Bottom sticky progress bar (desktop only) */}
          <div className="hidden md:block sticky bottom-4 mt-6">
            <div className="bg-[#151D29]/90 backdrop-blur-lg rounded-xl border border-white/5 shadow-xl flex items-center justify-between p-4">
              <div>
                <div className="text-sm font-medium">{status === 'completed' ? 'Completed' : status === 'started' ? 'Started' : 'Not Started'}</div>
                <div className="text-xs text-gray-400">{status === 'started' ? 'In progress' : status === 'completed' ? 'Finished' : 'Ready to begin'}</div>
              </div>
              <div className="flex items-center gap-2">
                {status === 'not_started' && (
                  <button onClick={ensureStarted} className="px-4 py-2 rounded-lg bg-[#9af7f1] text-gray-900 font-medium hover:opacity-90">Start</button>
                )}
                {status === 'started' && (
                  <button onClick={markCompleted} className="px-4 py-2 rounded-lg bg-[#9af7f1] text-gray-900 font-medium hover:opacity-90">Mark Complete</button>
                )}
                {status === 'completed' && (
                  <button onClick={markStarted} className="px-4 py-2 rounded-lg bg-[#1a2533] text-white hover:bg-[#232f3e]">Mark as Started</button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Admin ClassForm Modal (global) */}
      {isAdmin && showClassForm && (
        <ClassForm
          onClose={() => { setShowClassForm(false); setEditingClass(null); }}
          onSubmit={async (data, selectedCats, blueprintIds, toolIds) => {
            try {
              if (editingClass) {
                // Update existing class
                let thumbnailUrl = editingClass.thumbnail;
                if ((data.video_url && data.video_url !== editingClass.video_url)) {
                  const vid = extractVideoId(data.video_url);
                  if (vid) thumbnailUrl = `https://img.youtube.com/vi/${vid}/maxresdefault.jpg`;
                }
                const updated = {
                  ...editingClass,
                  ...data,
                  thumbnail: thumbnailUrl,
                } as ClassData;
                const { error } = await supabase
                  .from('classes')
                  .update({
                    title: updated.title,
                    description: updated.description,
                    video_url: updated.video_url,
                    thumbnail: updated.thumbnail,
                  })
                  .eq('id', updated.id);
                if (error) throw error;
                // Update categories
                await supabase.from('class_categories').delete().eq('class_id', updated.id);
                if (selectedCats.length > 0) {
                  await supabase.from('class_categories').insert(selectedCats.map(cid => ({ class_id: updated.id, category_id: cid })));
                }
                // Attachments
                await attachBlueprintsToClass(updated.id, blueprintIds);
                await attachToolsToClass(updated.id, toolIds);
                // Refresh local
                setClassData(updated);
                // Refresh displayed attachments
                const [bp, tl] = await Promise.all([getClassBlueprints(updated.id), getClassTools(updated.id)]);
                setBlueprints(bp);
                setTools(tl);
              } else {
                // Create new class
                let thumbnailUrl = '';
                const vid = data.video_url ? extractVideoId(data.video_url) : null;
                if (vid) thumbnailUrl = `https://img.youtube.com/vi/${vid}/maxresdefault.jpg`;
                const { data: created, error } = await supabase
                  .from('classes')
                  .insert([{ title: data.title, description: data.description, video_url: data.video_url, thumbnail: thumbnailUrl }])
                  .select();
                if (error) throw error;
                const newId = created?.[0]?.id as string;
                if (newId) {
                  if (selectedCats.length > 0) {
                    await supabase.from('class_categories').insert(selectedCats.map(cid => ({ class_id: newId, category_id: cid })));
                  }
                  await attachBlueprintsToClass(newId, blueprintIds);
                  await attachToolsToClass(newId, toolIds);
                  navigate(`/classroom/${newId}${folderId ? `?folder=${folderId}` : ''}`);
                }
              }
            } catch (e) {
              console.error('Save failed', e);
              alert('Failed to save class');
            } finally {
              setShowClassForm(false);
              setEditingClass(null);
            }
          }}
          categories={categories}
          editingClass={editingClass}
          isAdmin={isAdmin}
          initialCategoryIds={editingClass ? editingClass.categories : (folderId ? [folderId] : [])}
          initialBlueprintIds={editingClass ? blueprints.map(b => b.id) : []}
          initialToolIds={editingClass ? tools.map(t => t.id) : []}
        />
      )}
    </motion.div>
  );
};

export default ClassDetails;